import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { NgxSpinnerService } from 'ngx-spinner';
import { ToastrService } from 'ngx-toastr';
import { Subject, Subscription, debounceTime, distinctUntilChanged } from 'rxjs';
import { AppointmentsService } from '../service/appointments.service';

interface Appointment {
  appointment_id: string;
  client: any;
  lawyer: any;
  account_information?: any;
  appointment_time: string;
  appointment_from: string;
  appointment_status: string;
  payment_status: string;
  amount_paid: any;
  payment_breakdown: any;
  settlement_status: string;
  settlement_amount: any;
  settlement_due_at: string | null;
  settlement_utr?: string;
  settlement_notes?: string;
  settlement_date?: string;
  refund?: any;
  notes?: any[];
}

interface Option {
  value: string;
  label: string;
}

@Component({
  selector: 'app-appointments',
  templateUrl: './appointments.component.html',
  styleUrls: ['./appointments.component.css']
})
export class AppointmentsComponent implements OnInit, OnDestroy {

  // ═══════════════════════════════════════════════════════════
  //  State
  //
  //  Filtering, sorting and paging all happen on the server: the table, the
  //  summary cards and the dropdown options are three reads of one filter,
  //  so a card can never disagree with the rows under it. Nothing here
  //  filters a loaded page in memory.
  // ═══════════════════════════════════════════════════════════

  rows: Appointment[] = [];
  summary: any = null;
  currency: string = 'INR';

  // ─── The settlement card ──────────────────────────────────────────────────
  //
  // The card headlines one slice of the settlement at a time. The summary
  // already carries all three amounts, so switching between them is a local
  // toggle - it costs no request.
  settlementSlices: Option[] = [
    { value: 'completed', label: 'Success' },
    { value: 'pending', label: 'Pending' },
    { value: 'failed', label: 'Failed' }
  ];

  settlementSlice: string = 'completed';

  loading: boolean = false;
  summaryLoading: boolean = false;

  pagination: any = {
    page: 1,
    limit: 10,
    total: 0,
    total_pages: 0,
    showing: { from: 0, to: 0 }
  };

  sortBy: string = 'appointment_from';
  sortOrder: 'asc' | 'desc' = 'desc';

  // ─── The date control ─────────────────────────────────────────────────────
  //
  // Every option resolves to the same two values before anything is sent:
  // `startDate` and `endDate`, as IST calendar days. `datePreset` is only
  // which chip is lit - the API never sees it, so there is no preset
  // vocabulary shared across the wire to fall out of step.
  datePreset: string = 'today';
  startDate: string = '';
  endDate: string = '';

  // Drafts: the picker only takes effect on Apply, like the mock's date popover.
  draftStart: string = '';
  draftEnd: string = '';

  showDateMenu: boolean = false;

  // How many IST days back each preset opens and closes, counting today as 0.
  // `null` means no bound; `month` opens on the 1st, whichever day that is.
  readonly datePresets: { value: string; label: string; from: any; to: any }[] = [
    { value: 'today',        label: 'Today',        from: 0,       to: 0 },
    { value: 'yesterday',    label: 'Yesterday',    from: 1,       to: 1 },
    { value: 'last_7_days',  label: 'Last 7 Days',  from: 6,       to: 0 },
    { value: 'last_30_days', label: 'Last 30 Days', from: 29,      to: 0 },
    { value: 'this_month',   label: 'This Month',   from: 'month', to: 0 },
    { value: 'all',          label: 'All Time',     from: null,    to: null }
  ];

  // ─── The filter bar ───────────────────────────────────────────────────────
  //
  // `applied` is what the server is answering; `draft` is what the user is
  // building. Clear and Apply move between the two.
  applied = {
    appointment_status: [] as string[],
    payment_status: [] as string[],
    settlement_status: [] as string[],
    lawyer_id: [] as string[]
  };

  draft = {
    appointment_status: [] as string[],
    payment_status: [] as string[],
    settlement_status: [] as string[],
    lawyer_id: [] as string[]
  };

  openDropdown: string | null = null;

  searchQuery: string = '';

  // ─── Dropdown options, served by the filters endpoint ─────────────────────
  options: any = {
    appointment_status: [] as Option[],
    payment_status: [] as Option[],
    settlement_status: [] as Option[],
    lawyer: [] as Option[]
  };

  // ─── Row menus and modals ─────────────────────────────────────────────────
  activeMenuUid: string | null = null;
  selectedAccountId: string | null = null;

  showNotesModal: boolean = false;
  selectedAppointment: any = null;
  newNote: string = '';
  savingNote: boolean = false;

  showSettlementModal: boolean = false;
  selectedSettlementAppointment: any = null;
  utrNumber: string = '';
  settlementNotes: string = '';
  savingSettlement: boolean = false;

  showSettlementDetailsModal: boolean = false;
  selectedSettlement: any = null;

  showBankDetailsModal: boolean = false;
  selectedBank: any = null;

  private readonly search$ = new Subject<string>();
  private readonly subscriptions = new Subscription();

  // Only these two settlement states can be completed by hand: both are
  // stored as `initiated`, which is what the settlement endpoint requires.
  private readonly settleableStatuses = ['processing', 'failed'];

  constructor(
    private appointmentsService: AppointmentsService,
    private spinner: NgxSpinnerService,
    private toastr: ToastrService
  ) {}

  ngOnInit(): void {
    // Typing must not put a request on the wire per keystroke.
    this.subscriptions.add(
      this.search$
        .pipe(debounceTime(400), distinctUntilChanged())
        .subscribe(() => {
          this.pagination.page = 1;
          this.reload();
        })
    );

    // Resolve the default preset into real dates before the first read.
    this.applyPreset(this.datePreset);
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  // Any outside click closes whichever popover is open.
  @HostListener('document:click')
  closeMenus(): void {
    this.showDateMenu = false;
    this.openDropdown = null;
    this.activeMenuUid = null;
    this.selectedAccountId = null;
  }

  // ═══════════════════════════════════════════════════════════
  //  API
  // ═══════════════════════════════════════════════════════════

  /**
   * The one filter every call reads. `page`, `limit` and the sort are the
   * table's alone; the summary and the dropdowns ignore them.
   */
  private params(includePaging: boolean = true): any {
    const params: any = {
      start_date: this.startDate,
      end_date: this.endDate,
      search: this.searchQuery.trim(),
      ...this.applied
    };

    if (includePaging) {
      params.page = this.pagination.page;
      params.limit = this.pagination.limit;
      params.sort_by = this.sortBy;
      params.sort_order = this.sortOrder;
    }

    return params;
  }

  /** The table and the cards, fetched together so they land together. */
  reload(): void {
    this.loadAppointments();
    this.loadSummary();
  }

  loadAppointments(): void {
    this.loading = true;
    this.spinner.show();

    this.appointmentsService.getAppointments(this.params()).subscribe({
      next: (resp: any) => {
        const data = resp?.['data'] || {};

        this.rows = data['appointments'] || [];
        this.pagination = data['pagination'] || this.pagination;
        this.currency = data['currency'] || this.currency;

        this.loading = false;
        this.spinner.hide();
      },
      error: (err: any) => {
        this.loading = false;
        this.spinner.hide();
        this.toastr.error(
          err?.error?.msg || 'Unable to load appointments.'
        );
      }
    });
  }

  loadSummary(): void {
    this.summaryLoading = true;

    this.appointmentsService.getSummary(this.params(false)).subscribe({
      next: (resp: any) => {
        this.summary = resp?.['data'] || null;
        this.summaryLoading = false;
      },
      error: () => {
        this.summaryLoading = false;
      }
    });
  }

  /**
   * The dropdown contents.
   *
   * Only the date range narrows them - not the other dropdowns - so picking
   * one lawyer cannot empty the lawyer list. That is also why this is not
   * refetched on every Apply: only a date change can change the options.
   */
  loadFilterOptions(): void {
    this.appointmentsService.getFilterOptions({
      start_date: this.startDate,
      end_date: this.endDate
    }).subscribe({
      next: (resp: any) => {
        const data = resp?.['data'] || {};

        Object.keys(this.options).forEach(key => {
          this.options[key] = data[key] || [];
        });
      },
      error: () => {
        // The screen still works with empty dropdowns; no need to shout.
      }
    });
  }

  // ═══════════════════════════════════════════════════════════
  //  The settlement card
  // ═══════════════════════════════════════════════════════════

  /** The headline amount: whichever slice is selected. */
  get settlementAmount(): any {
    return this.summary?.['settlement']?.[this.settlementSlice + '_amount'];
  }

  /** How many settlements that slice covers. */
  settlementCount(slice: string): number {
    return this.summary?.['settlement']?.[slice] || 0;
  }

  selectSettlementSlice(slice: string): void {
    this.settlementSlice = slice;
  }

  // ═══════════════════════════════════════════════════════════
  //  The date control
  // ═══════════════════════════════════════════════════════════

  get dateRangeLabel(): string {
    if (!this.startDate && !this.endDate) {
      return 'All Time';
    }

    const from = this.formatDate(this.startDate);
    const to = this.formatDate(this.endDate);

    if (!this.startDate) {
      return `Up to ${to}`;
    }

    if (!this.endDate) {
      return `From ${from}`;
    }

    return from === to ? from : `${from} - ${to}`;
  }

  /**
   * Today in IST as `YYYY-MM-DD`, offset by a number of days.
   *
   * The API reads a bare date as an IST calendar day, so the day has to be
   * worked out in IST too - otherwise a browser in another timezone would
   * ask for the wrong one either side of midnight.
   */
  private istDay(daysBack: number = 0): string {
    const now = new Date();
    const ist = new Date(now.getTime() - daysBack * 86400000);

    return ist.toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' });
  }

  /** Turn a preset into the two dates it stands for, then read. */
  applyPreset(preset: string): void {
    const option = this.datePresets.find(item => item.value === preset);

    this.datePreset = preset;

    if (!option || option.from === null) {
      // All Time: both bounds dropped, which the API reads as no filter.
      this.startDate = '';
      this.endDate = '';
    } else if (option.from === 'month') {
      const today = this.istDay(0);

      this.startDate = today.slice(0, 8) + '01';
      this.endDate = this.istDay(option.to);
    } else {
      this.startDate = this.istDay(option.from);
      this.endDate = this.istDay(option.to);
    }

    this.draftStart = this.startDate;
    this.draftEnd = this.endDate;

    this.pagination.page = 1;
    this.loadFilterOptions();
    this.reload();
  }

  selectPreset(preset: string): void {
    this.applyPreset(preset);
  }

  toggleDateMenu(event: Event): void {
    event.stopPropagation();

    this.showDateMenu = !this.showDateMenu;
    this.openDropdown = null;

    if (this.showDateMenu) {
      this.draftStart = this.startDate;
      this.draftEnd = this.endDate;
    }
  }

  applyDateRange(): void {
    if (this.draftStart && this.draftEnd && this.draftStart > this.draftEnd) {
      this.toastr.warning('The start date cannot be after the end date.');
      return;
    }

    this.startDate = this.draftStart;
    this.endDate = this.draftEnd;
    this.datePreset = 'custom';

    this.showDateMenu = false;
    this.pagination.page = 1;

    this.loadFilterOptions();
    this.reload();
  }

  clearDateRange(): void {
    this.showDateMenu = false;
    this.applyPreset('today');
  }

  // ═══════════════════════════════════════════════════════════
  //  The filter bar
  // ═══════════════════════════════════════════════════════════

  toggleDropdown(name: string, event: Event): void {
    event.stopPropagation();

    this.openDropdown = this.openDropdown === name ? null : name;
    this.showDateMenu = false;
  }

  isChecked(field: string, value: string): boolean {
    return (this.draft as any)[field].indexOf(value) > -1;
  }

  toggleOption(field: string, value: string): void {
    const selected: string[] = (this.draft as any)[field];
    const index = selected.indexOf(value);

    if (index > -1) {
      selected.splice(index, 1);
    } else {
      selected.push(value);
    }
  }

  /** The label a closed dropdown shows: the placeholder, or what is picked. */
  dropdownLabel(field: string, placeholder: string): string {
    const selected: string[] = (this.applied as any)[field];

    if (!selected.length) {
      return placeholder;
    }

    if (selected.length === 1) {
      return this.optionLabel(field, selected[0]);
    }

    return `${placeholder} (${selected.length})`;
  }

  private optionLabel(field: string, value: string): string {
    const key = field === 'lawyer_id' ? 'lawyer' : field;

    const option = (this.options[key] || []).find(
      (item: Option) => item.value === value
    );

    return option ? option.label : this.humanise(value);
  }

  get activeFilterCount(): number {
    return Object.keys(this.applied).reduce(
      (count, key) => count + (this.applied as any)[key].length,
      0
    );
  }

  applyFilters(): void {
    Object.keys(this.draft).forEach(key => {
      (this.applied as any)[key] = [...(this.draft as any)[key]];
    });

    this.openDropdown = null;
    this.pagination.page = 1;

    this.reload();
  }

  clearFilters(): void {
    Object.keys(this.draft).forEach(key => {
      (this.draft as any)[key] = [];
      (this.applied as any)[key] = [];
    });

    this.searchQuery = '';
    this.openDropdown = null;
    this.pagination.page = 1;

    this.reload();
  }

  onSearch(): void {
    this.search$.next(this.searchQuery);
  }

  clearSearch(): void {
    this.searchQuery = '';
    this.pagination.page = 1;
    this.reload();
  }

  // ═══════════════════════════════════════════════════════════
  //  Sorting and paging
  // ═══════════════════════════════════════════════════════════

  sortColumn(field: string): void {
    if (this.sortBy === field) {
      this.sortOrder = this.sortOrder === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortBy = field;
      this.sortOrder = 'desc';
    }

    this.pagination.page = 1;
    this.loadAppointments();
  }

  getSortIcon(field: string): string {
    if (this.sortBy !== field) {
      return 'bi-arrow-down-up';
    }

    return this.sortOrder === 'asc' ? 'bi-sort-down-alt' : 'bi-sort-down';
  }

  getTotalPages(): number {
    return this.pagination?.total_pages || 0;
  }

  getPageNumbers(): number[] {
    const total = this.getTotalPages();
    const current = this.pagination.page;

    if (total <= 1) {
      return total === 1 ? [1] : [];
    }

    // A five-page window that stays inside the available range.
    let start = Math.max(1, current - 2);
    const end = Math.min(total, start + 4);

    start = Math.max(1, end - 4);

    const pages: number[] = [];

    for (let page = start; page <= end; page++) {
      pages.push(page);
    }

    return pages;
  }

  goToPage(page: number): void {
    if (
      page < 1 ||
      page > this.getTotalPages() ||
      page === this.pagination.page
    ) {
      return;
    }

    this.pagination.page = page;
    this.loadAppointments();
  }

  goToPrevPage(): void {
    this.goToPage(this.pagination.page - 1);
  }

  goToNextPage(): void {
    this.goToPage(this.pagination.page + 1);
  }

  changePageSize(limit: any): void {
    this.pagination.limit = Number(limit);
    this.pagination.page = 1;
    this.loadAppointments();
  }

  getPaginationRange(): string {
    const total = this.pagination?.total || 0;

    if (!total) {
      return 'No appointments';
    }

    const showing = this.pagination.showing || {};

    return `Showing ${showing['from'] || 0} to ${showing['to'] || 0} of ${total} appointments`;
  }

  trackByAppointment(_: number, row: Appointment): string {
    return row.appointment_id;
  }

  // ═══════════════════════════════════════════════════════════
  //  Row menus
  // ═══════════════════════════════════════════════════════════

  toggleActionMenu(id: string, event: Event): void {
    event.stopPropagation();

    this.activeMenuUid = this.activeMenuUid === id ? null : id;
    this.selectedAccountId = null;
  }

  openAccountCard(appointment: Appointment, event: MouseEvent): void {
    event.stopPropagation();

    this.selectedAccountId =
      this.selectedAccountId === appointment.appointment_id
        ? null
        : appointment.appointment_id;
  }

  closeAccountCard(): void {
    this.selectedAccountId = null;
  }

  hasBankDetails(appointment: Appointment): boolean {
    return !!appointment.account_information?.account_number?.trim();
  }

  canSettle(appointment: Appointment): boolean {
    return this.settleableStatuses.indexOf(appointment.settlement_status) > -1;
  }

  // ═══════════════════════════════════════════════════════════
  //  Notes
  // ═══════════════════════════════════════════════════════════

  openNotesModal(appointment: Appointment): void {
    this.selectedAppointment = { ...appointment };
    this.newNote = '';
    this.showNotesModal = true;
  }

  closeNotesModal(): void {
    this.showNotesModal = false;
    this.selectedAppointment = null;
    this.newNote = '';
  }

  saveNote(): void {
    const note = this.newNote.trim();

    if (!note || this.savingNote) {
      return;
    }

    this.savingNote = true;
    this.spinner.show();

    this.appointmentsService
      .addNote(this.selectedAppointment.appointment_id, { note })
      .subscribe({
        next: () => {
          this.savingNote = false;
          this.spinner.hide();
          this.toastr.success('Note saved successfully.');
          this.closeNotesModal();
          this.loadAppointments();
        },
        error: (err: any) => {
          this.savingNote = false;
          this.spinner.hide();
          this.toastr.error(err?.error?.msg || 'Unable to save note.');
        }
      });
  }

  // ═══════════════════════════════════════════════════════════
  //  Settlement
  // ═══════════════════════════════════════════════════════════

  openSettlementModal(appointment: Appointment): void {
    this.selectedSettlementAppointment = appointment;
    this.utrNumber = '';
    this.settlementNotes = '';
    this.showSettlementModal = true;
  }

  closeSettlementModal(): void {
    this.showSettlementModal = false;
    this.selectedSettlementAppointment = null;
    this.utrNumber = '';
    this.settlementNotes = '';
  }

  saveSettlement(): void {
    const utr = this.utrNumber.trim();

    if (!utr || this.savingSettlement) {
      return;
    }

    this.savingSettlement = true;
    this.spinner.show();

    this.appointmentsService
      .updateSettlement(this.selectedSettlementAppointment.appointment_id, {
        settlement_utr: utr,
        settlement_notes: this.settlementNotes
      })
      .subscribe({
        next: () => {
          this.savingSettlement = false;
          this.spinner.hide();
          this.toastr.success('Settlement status updated successfully.');
          this.closeSettlementModal();

          // The settlement cards move with the row, so reload both.
          this.reload();
        },
        error: (err: any) => {
          this.savingSettlement = false;
          this.spinner.hide();
          this.toastr.error(
            err?.error?.msg || 'Unable to update settlement status.'
          );
        }
      });
  }

  openSettlementDetails(appointment: any): void {
    this.selectedSettlement = appointment;
    this.showSettlementDetailsModal = true;
  }

  closeSettlementDetails(): void {
    this.showSettlementDetailsModal = false;
    this.selectedSettlement = null;
  }

  openBankDetails(appointment: any): void {
    this.selectedBank = appointment;
    this.showBankDetailsModal = true;
  }

  closeBankDetails(): void {
    this.showBankDetailsModal = false;
    this.selectedBank = null;
  }

  // ═══════════════════════════════════════════════════════════
  //  Display helpers
  //
  //  The API returns status values, never chip text: the labels come from
  //  the filter-options endpoint, so there is one source for both.
  // ═══════════════════════════════════════════════════════════

  private humanise(value: string): string {
    return (value || '')
      .split('_')
      .map(part => part.charAt(0).toUpperCase() + part.slice(1))
      .join(' ');
  }

  private labelFrom(group: string, value: string): string {
    if (!value) {
      return '-';
    }

    const option = (this.options[group] || []).find(
      (item: Option) => item.value === value
    );

    return option ? option.label : this.humanise(value);
  }

  appointmentStatusLabel(status: string): string {
    return this.labelFrom('appointment_status', status);
  }

  paymentStatusLabel(status: string): string {
    return this.labelFrom('payment_status', status);
  }

  settlementStatusLabel(status: string): string {
    return this.labelFrom('settlement_status', status);
  }

  getStatusClass(status: string): string {
    switch ((status || '').toLowerCase()) {
      case 'completed':
        return 'status-active';
      case 'confirmed':
        return 'status-confirmed';
      case 'ongoing':
        return 'status-ongoing';
      case 'pending_payment':
      case 'cancellation_pending':
      case 'resheduled':
        return 'status-pending';
      case 'cancelled':
      case 'no_show':
        return 'status-cancelled';
      case 'abandoned':
        return 'settlement-failed';
      default:
        return 'status-default';
    }
  }

  getPaymentStatusClass(status: string): string {
    switch ((status || '').toLowerCase()) {
      case 'paid':
        return 'status-active';
      case 'pending':
        return 'status-pending';
      case 'failed':
        return 'status-cancelled';
      case 'refunded':
        return 'status-refunded';
      default:
        return 'status-default';
    }
  }

  getSettlementStatusClass(status: string): string {
    switch ((status || '').toLowerCase()) {
      case 'pending':
        return 'settlement-pending';
      case 'processing':
        return 'settlement-initiated';
      case 'completed':
        return 'settlement-completed';
      case 'failed':
        return 'settlement-failed';
      case 'refunded':
        return 'status-refunded';
      default:
        return '';
    }
  }

  money(amount: any, currency: string = ''): string {
    if (amount === null || amount === undefined || amount === '') {
      return '-';
    }

    const value = Number(amount);

    if (isNaN(value)) {
      return '-';
    }

    const code = currency || this.currency || 'INR';
    const symbol = code === 'INR' ? '₹' : `${code} `;

    return symbol + value.toLocaleString('en-IN', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2
    });
  }

  formatDate(value: string | null | undefined): string {
    if (!value) {
      return '-';
    }

    const date = new Date(value);

    if (isNaN(date.getTime())) {
      return '-';
    }

    return date.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  }

  formatDateTime(value: string | null | undefined): string {
    if (!value) {
      return '-';
    }

    const date = new Date(value);

    if (isNaN(date.getTime())) {
      return '-';
    }

    return date.toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  }
}
